// src/pages/sow-draft/VersionHistoryModal.jsx
import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { ArrowLeft, Bot, FileText, History, Pencil, RefreshCw } from "lucide-react";
import { Button, EmptyState, Modal, SkeletonList } from "../../shared/ui";

const ACTION_ICON = {
  created: FileText,
  generated: Bot,
  edited: Pencil,
  regenerated: RefreshCw,
  restored: History,
};

const ACTION_LABEL = {
  created: "Created",
  generated: "Generated",
  edited: "Edited",
  regenerated: "Regenerated",
  restored: "Restored",
};

// Read-only counterpart of DraftReviewStep.jsx's own section-tab bar +
// content panel -- same numbered-circle tab pattern, same shui-input
// styling for the content area, so a version looks like the same page,
// just not editable (a plain readOnly textarea, not disabled, so the text
// stays selectable/copyable).
function VersionDetailView({ versionId, detail, loading, error, isCurrent, restoring, onBack, onRestore }) {
  // No effect needed to default this -- VersionDetailView is only ever
  // mounted fresh per version (the parent unmounts it on Back before a
  // different version can be opened), so selectedSectionId starts null
  // and this fallback to the first section covers that first render, same
  // pattern useSowDraft.js's own selectedSection derivation already uses.
  const [selectedSectionId, setSelectedSectionId] = useState(null);
  const selectedSection =
    detail?.sections?.find((s) => s.section_id === selectedSectionId) || detail?.sections?.[0];

  return (
    <div className="flex flex-col gap-3">
      <button
        type="button"
        onClick={onBack}
        className="flex items-center gap-1.5 text-sm font-medium text-brand-600 hover:underline w-fit"
      >
        <ArrowLeft size={14} /> Back to versions
      </button>

      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-muted">
          {detail ? (
            <>
              {new Date(detail.created_at).toLocaleString()} · {ACTION_LABEL[detail.action] || detail.action}
              {detail.sections?.length ? ` · ${detail.sections.length} sections` : ""}
            </>
          ) : (
            `v${versionId}`
          )}
        </p>
        <Button size="sm" variant="secondary" onClick={onRestore} disabled={restoring || isCurrent}>
          {isCurrent ? "Current" : "Restore"}
        </Button>
      </div>

      {loading ? (
        <SkeletonList rows={4} />
      ) : error ? (
        <p className="text-sm text-status-critical">Could not load this version's content.</p>
      ) : (
        <>
          <div className="flex gap-2 flex-wrap">
            {detail.sections.map((s, idx) => {
              const isActive = selectedSection?.section_id === s.section_id;
              return (
                <button
                  key={s.section_id}
                  type="button"
                  onClick={() => setSelectedSectionId(s.section_id)}
                  className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors ${
                    isActive ? "border-brand-600 bg-brand-50 text-brand-600" : "border-line text-ink hover:bg-gray-50"
                  }`}
                >
                  <span
                    className={`flex items-center justify-center w-5 h-5 rounded-full text-xs font-semibold ${
                      isActive ? "bg-brand-600 text-white" : "bg-gray-100 text-muted"
                    }`}
                  >
                    {idx + 1}
                  </span>
                  {s.title}
                </button>
              );
            })}
          </div>

          <div>
            <h4 className="text-sm font-semibold text-ink mb-2">{selectedSection?.title}</h4>
            <textarea
              className="shui-input w-full"
              style={{ minHeight: 280 }}
              value={selectedSection?.content || ""}
              readOnly
            />
          </div>
        </>
      )}
    </div>
  );
}

VersionDetailView.propTypes = {
  versionId: PropTypes.string.isRequired,
  detail: PropTypes.shape({
    action: PropTypes.string,
    created_at: PropTypes.string,
    sections: PropTypes.arrayOf(
      PropTypes.shape({
        section_id: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired,
        content: PropTypes.string,
      })
    ),
  }),
  loading: PropTypes.bool.isRequired,
  error: PropTypes.bool.isRequired,
  isCurrent: PropTypes.bool.isRequired,
  restoring: PropTypes.bool.isRequired,
  onBack: PropTypes.func.isRequired,
  onRestore: PropTypes.func.isRequired,
};

export default function VersionHistoryModal({
  open,
  onClose,
  currentVersion,
  onFetchVersions,
  onRestore,
  onFetchVersionDetail,
}) {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [viewingVersionId, setViewingVersionId] = useState(null);
  const [versionDetail, setVersionDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(false);

  useEffect(() => {
    setViewingVersionId(null);
    if (!open) return;
    setLoading(true);
    onFetchVersions()
      .then((list) => setVersions(list || []))
      .catch(() => setVersions([]))
      .finally(() => setLoading(false));
  }, [open, onFetchVersions]);

  const handleRestore = async (version) => {
    if (!window.confirm("Restore this version? Your current changes will be saved as a new version first.")) return;
    setRestoring(true);
    try {
      await onRestore(version);
      onClose();
    } finally {
      setRestoring(false);
    }
  };

  const handleView = async (version) => {
    setViewingVersionId(version.version);
    setVersionDetail(null);
    setDetailError(false);
    setDetailLoading(true);
    try {
      const detail = await onFetchVersionDetail(version.version);
      setVersionDetail(detail);
    } catch {
      setDetailError(true);
    } finally {
      setDetailLoading(false);
    }
  };

  const reversed = [...versions].reverse();
  const isViewingCurrent = viewingVersionId && reversed[0]?.version === viewingVersionId;

  return (
    <Modal
      open={open}
      title={viewingVersionId ? `Version v${viewingVersionId}` : "Version History"}
      onClose={onClose}
      size="lg"
    >
      {viewingVersionId ? (
        <VersionDetailView
          versionId={viewingVersionId}
          detail={versionDetail}
          isCurrent={isViewingCurrent}
          loading={detailLoading}
          error={detailError}
          restoring={restoring}
          onBack={() => setViewingVersionId(null)}
          onRestore={() => handleRestore(viewingVersionId)}
        />
      ) : (
        <>
          <p className="text-sm text-muted mb-4">
            Current version: <strong className="text-ink">v{currentVersion}</strong>
          </p>

          {loading ? (
            <SkeletonList rows={4} />
          ) : versions.length === 0 ? (
            <EmptyState icon={History} title="No version history yet" description="Versions appear here once the draft changes." />
          ) : (
            <ul className="flex flex-col gap-2">
              {reversed.map((v, idx) => {
                const isCurrent = idx === 0;
                const Icon = ACTION_ICON[v.action] || FileText;
                return (
                  <li
                    key={v.version}
                    onClick={() => handleView(v)}
                    className={`flex items-center justify-between gap-3 rounded-lg border p-3 cursor-pointer hover:bg-brand-50/60 ${
                      isCurrent ? "border-brand-600 bg-brand-50" : "border-line"
                    }`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <Icon size={16} className="text-muted shrink-0" />
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-ink">v{v.version}</span>
                          {isCurrent && (
                            <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-brand-600 text-white">
                              Current
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-muted">
                          {new Date(v.created_at).toLocaleString()} · {ACTION_LABEL[v.action] || v.action}
                          {v.section_count ? ` · ${v.section_count} sections` : ""}
                        </p>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRestore(v.version);
                      }}
                      disabled={restoring || isCurrent}
                    >
                      {isCurrent ? "Current" : "Restore"}
                    </Button>
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}
    </Modal>
  );
}

VersionHistoryModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  currentVersion: PropTypes.string.isRequired,
  onFetchVersions: PropTypes.func.isRequired,
  onRestore: PropTypes.func.isRequired,
  onFetchVersionDetail: PropTypes.func.isRequired,
};
